import { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    FluidImage,
    Icon,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    SelectionSetTileContents,
    SelectionSetTileDescription,
    SelectionSetTileImageContainer,
    SelectionSetTileName,
    getTokenCssVar,
} from '@vp/swan';
import type { SelectedValue } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { SelectorCard } from '../SelectorCard';
import type { StyleTileOption, StyleTilesOptions } from './parseOptions';
import type { StyleTilesReply } from './parseReply';

export type StyleTilesSelectorViewProps = {
    options: StyleTilesOptions;
    onSubmit: (reply: StyleTilesReply) => void;
    /**
     * Asks the assistant to explain the choices. Required, not optional: the footer lays the help
     * and Continue actions out with `space-between`, so an absent help button would silently pull
     * Continue to the left edge rather than degrade gracefully.
     */
    onRequestHelp: () => void;
};

function toReply(option: StyleTileOption): StyleTilesReply {
    return { value: option.value, name: option.name, imageUrl: option.imageUrl };
}

const TILE_IMAGE_WIDTH = '120px';
const TILE_IMAGE_HEIGHT = '90px';
const SELECTOR_SCROLL_MAX_HEIGHT = '320px';

/**
 * The style-tiles composer selector.
 *
 * Renders only the card. Where that card sits — the composer's background band and centred
 * max-width column — belongs to whatever hosts the composer, exactly as it does for ChatInput.
 * A selector that positioned itself would have to agree with the host on those values, and
 * would double them up when mounted in the composer slot.
 */
export function StyleTilesSelectorView({
    options,
    onSubmit,
    onRequestHelp,
}: StyleTilesSelectorViewProps) {
    const [selectedValue, setSelectedValue] = useState<SelectedValue>(options.preselected ?? null);
    const [helpRequested, setHelpRequested] = useState(false);
    const headingRef = useRef<HTMLElement>(null);

    useEffect(() => {
        requestAnimationFrame(() => {
            headingRef.current?.focus();
        });
    }, []);

    const title = options.title?.trim() ? options.title : __('Which designs do you like?');

    const handleContinue = () => {
        const option = options.options.find((candidate) => candidate.value === selectedValue);
        if (option) onSubmit(toReply(option));
    };

    // One help request per selector: the answer arrives as the next assistant turn, and a second
    // identical request before then would just duplicate the message in the thread.
    const handleRequestHelp = () => {
        setHelpRequested(true);
        onRequestHelp();
    };

    return (
        <SelectorCard
            title={title}
            subtitle={`(${__('Please select 1')})`}
            headingRef={headingRef}
            footer={
                <>
                    <Button
                        skin="tertiary"
                        iconPosition="left"
                        onClick={handleRequestHelp}
                        disabled={helpRequested}
                    >
                        <Icon iconType="aiSupport" />
                        {__('Help me answer')}
                    </Button>
                    <Button skin="secondary" onClick={handleContinue} disabled={!selectedValue}>
                        {__('Continue')}
                    </Button>
                </>
            }
        >
            <Box
                style={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    maxHeight: SELECTOR_SCROLL_MAX_HEIGHT,
                    padding: getTokenCssVar('SwanSemSpace1'),
                }}
            >
                <SelectionSet
                    skin="tiles-mini"
                    variant="single-select"
                    selectedValue={selectedValue}
                    onSelectedValueChange={setSelectedValue}
                    aria-label={title}
                >
                    {options.options.map((option) => (
                        <SelectionSetInput key={option.value} value={option.value}>
                            <SelectionSetLabel>
                                <SelectionSetTileImageContainer
                                    style={{
                                        width: TILE_IMAGE_WIDTH,
                                        height: TILE_IMAGE_HEIGHT,
                                        minWidth: TILE_IMAGE_WIDTH,
                                        flexShrink: 0,
                                    }}
                                >
                                    <FluidImage
                                        src={option.imageUrl}
                                        alt={option.name}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </SelectionSetTileImageContainer>
                                <SelectionSetTileContents>
                                    <SelectionSetTileName>{option.name}</SelectionSetTileName>
                                    {option.description && (
                                        <SelectionSetTileDescription>
                                            {option.description}
                                        </SelectionSetTileDescription>
                                    )}
                                </SelectionSetTileContents>
                            </SelectionSetLabel>
                        </SelectionSetInput>
                    ))}
                </SelectionSet>
            </Box>
        </SelectorCard>
    );
}
